import { useCallback, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import i18n from 'i18n';
import cx from 'classnames/bind';
import { FormConfig, FormSection } from 'components/common/form/FormConfig';
import { FormikCustomRenderProps } from 'components/common/form/field/CustomField';
import {
  BeaconConnectionOption,
  BeaconMessageFormType
} from 'core/beaconMessage/BeaconMessage';
import styles from './creativeSetupSection.module.scss';
import { useLineMessageFields } from 'hooks/LineMessage';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import { validateEmpty } from 'utils/ValidateUtils';
import { LineMessageType } from 'core/advertiser/LineBeacon';
import { useStoreNumOptions } from './useStoreNumOptions';

const classNames = cx.bind(styles);

export const useCreativeSetupSection = (
  beaconConnectionOptions: BeaconConnectionOption[]
): {
  loading: boolean;
  section: FormSection;
} => {
  const { values, setFieldValue } = useFormikContext<BeaconMessageFormType>();

  const advertiserId = useMemo(() => {
    const beaconConnectionId =
      values.creative.typeProperties.beaconConnection?.value;
    const beaconConnectionOption = beaconConnectionOptions.find(
      (option) => option.value === beaconConnectionId
    );
    return beaconConnectionOption ? beaconConnectionOption.advertiserId : null;
  }, [
    values.creative.typeProperties.beaconConnection?.value,
    beaconConnectionOptions
  ]);

  const lineMessageFields = useLineMessageFields(
    BeaconType.LINE_SIMPLE_BEACON,
    advertiserId,
    false,
    'creative.'
  );

  const { intervalHours, maxFrequency } = values.creative.typeProperties;

  const [showFrequencyCapSetting, setShowFrequencyCapSetting] =
    useState<boolean>(intervalHours > 0 && maxFrequency > 0);

  const onFrequencyCapSwitchChange = useCallback(
    (event) => {
      const value = event.target.checked;
      setShowFrequencyCapSetting(value);
      if (!value) {
        setFieldValue('creative.typeProperties.intervalHours', 0);
        setFieldValue('creative.typeProperties.maxFrequency', 0);
      } else {
        setFieldValue('creative.typeProperties.intervalHours', 5);
        setFieldValue('creative.typeProperties.maxFrequency', 1);
      }
    },
    [setFieldValue]
  );

  const renderFrequencyControlInput = useCallback(
    (props: FormikCustomRenderProps) => {
      const onMaxFrequencyChange = (e) => {
        props.setFieldValue(
          'creative.typeProperties.maxFrequency',
          e.target.value < 0 ? 0 : e.target.value
        );
      };
      const onIntervalHoursChange = (e) => {
        props.setFieldValue(
          'creative.typeProperties.intervalHours',
          e.target.value < 0 ? 0 : e.target.value
        );
      };
      const errorMaxFrequency = get(
        props.errors,
        'creative.typeProperties.maxFrequency'
      );
      const errorintervalHours = get(
        props.errors,
        'creative.typeProperties.intervalHours'
      );
      const maxFrequencyClass = classNames('maxFrequency', {
        error: errorMaxFrequency
      });
      const intervalHoursClass = classNames('intervalHours', {
        error: errorintervalHours
      });
      const timeUnitKey = +intervalHours > 1 ? 'hours' : 'hour';
      const cappingCountKey =
        +maxFrequency > 1 ? 'cappingCounts' : 'cappingCount';
      return (
        <div className={styles.frequencyControl}>
          <div className={styles.intervalHoursContainer}>
            {i18n.t('beaconFields.labels.frequencyCap.every') || ''}
            <input
              className={intervalHoursClass}
              name='creative.typeProperties.intervalHours'
              value={intervalHours}
              onChange={onIntervalHoursChange}
              min={1}
              type='number'
            />
            {i18n.t(`common.units.${timeUnitKey}`)}
          </div>
          <div>
            {i18n.t('beaconFields.labels.frequencyCap.send')}
            <input
              className={maxFrequencyClass}
              value={maxFrequency}
              name='creative.typeProperties.maxFrequency'
              onChange={onMaxFrequencyChange}
              min={1}
              type='number'
            />
            {i18n.t(`beaconFields.labels.frequencyCap.${cappingCountKey}`)}
          </div>
          {errorMaxFrequency && (
            <div className='errorTip'>{errorMaxFrequency as string}</div>
          )}
          {errorintervalHours && (
            <div className='errorTip'>{errorintervalHours as string}</div>
          )}
        </div>
      );
    },
    [intervalHours, maxFrequency]
  );

  const onBeaconConnectionChange = useCallback(() => {
    setFieldValue('creative.lineMessage', [
      {
        type: LineMessageType.TEXT,
        message: ''
      }
    ]);
  }, [setFieldValue]);

  const beaconConnectionId =
    values.creative.typeProperties.beaconConnection?.value;

  const { loading, storeNumOptions } = useStoreNumOptions(
    values.channelId,
    beaconConnectionId
  );

  const section = useMemo(() => {
    const fieldsBuilder = new FormConfig.FieldsBuilder();
    if (
      beaconConnectionId &&
      beaconConnectionOptions.length === 1 &&
      beaconConnectionOptions[0].value === beaconConnectionId
    ) {
      fieldsBuilder.addFormikLabel({
        label: i18n.t('beaconFields.labels.beaconConnection'),
        name: 'creative.typeProperties.beaconConnection.label'
      });
    } else {
      fieldsBuilder.addFormikSelect({
        label: i18n.t('beaconFields.labels.beaconConnection'),
        name: 'creative.typeProperties.beaconConnection',
        options: beaconConnectionOptions,
        onChange: onBeaconConnectionChange,
        validate: validateEmpty
      });
    }

    fieldsBuilder.addFormikSelect({
      label: i18n.t('beaconMessageFields.labels.deviceSetting'),
      name: 'creative.typeProperties.stores',
      options: storeNumOptions,
      simpleValue: true,
      isMulti: true
    });
    fieldsBuilder.addSwitch({
      label: i18n.t('beaconMessageFields.labels.frequencyCapSwitch'),
      name: 'frequencyCapSwitch',
      value: showFrequencyCapSetting,
      onChange: onFrequencyCapSwitchChange
    });

    if (showFrequencyCapSetting) {
      fieldsBuilder.addFormikCustom({
        label: '',
        name: 'frequencyControl',
        render: renderFrequencyControlInput
      });
    }

    return new FormConfig.SectionBuilder([
      ...fieldsBuilder.build(),
      ...lineMessageFields
    ])
      .withTitle(i18n.t('beaconMessageSetupFlowPage.titles.creativeSection'))
      .build();
  }, [
    beaconConnectionOptions,
    lineMessageFields,
    showFrequencyCapSetting,
    beaconConnectionId,
    storeNumOptions,
    onBeaconConnectionChange,
    onFrequencyCapSwitchChange,
    renderFrequencyControlInput
  ]);

  return {
    loading,
    section
  };
};
